/* @jsxImportSource @emotion/react */
import { css } from '@emotion/react/macro'
import colors from '@sportninja/common/constants/appColors'
import {
  getAllGenericSportProperties,
  getAllPlayerTypes,
  getSports,
} from '@sportninja/common/selectors/types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { Spin, Table, Tooltip } from 'antd'
import { getImageThumbnailId } from '@sportninja/common/utils/utils'
import { Link } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import req from '@sportninja/common/api/request'

/**
 * Interface representing a player's image data.
 * @interface PlayerImage
 */
interface PlayerImage {
  id: string
  filename: string
  full_path: string
  mime_type: string
  width: number
  height: number
}

/**
 * Interface representing a team associated with a player.
 * @interface PlayerTeam
 */
interface PlayerTeam {
  player_type_id: number
  player_type_name: string
  default_player_type_id: number
  default_player_type_name: string
  player_number: number
  id: string
  name: string
  image: string
  sport_id: string
  sport_name: string
  generic_sport_id: string | null
  generic_sport_name: string | null
  is_goalie: boolean
  is_player: boolean
}

/**
 * Interface representing detailed player information.
 * @interface DetailedPlayer
 */
interface DetailedPlayer {
  id: string
  name_first: string
  name_middle: string | null
  name_last: string
  full_name: string | null
  height: string
  weight: number
  gender: string | null
  image: PlayerImage
  is_goalie: boolean
  is_skater: boolean
  teams: PlayerTeam[]
  player_number: number | null
  play_direction: string | null
  is_affiliate: boolean
  is_injured: boolean
  is_playing: boolean
  is_starting: boolean
  is_proposed_starting: boolean
  is_suspended: boolean
  attendance_status: number | null
  customer_identifier: string
  suspensions: any[]
  is_signed_waiver: boolean
  league_registration_number: string | null
  player_type: any | null
}

/**
 * Interface representing a sport with detailed properties and configurations.
 * @interface Sport
 */
interface Sport {
  id: string
  name: string
  name_full: string
  assists: Array<{
    id: string
    name: string
    name_full: string
  }>
  penalties: Array<{
    id: string
    name: string
    name_full: string
  }>
  offenses: Array<{
    id: string
    name: string
    name_full: string
    default_offense_severity_id: number
  }>
  offenseSeverityTypes: Array<any>
  players: Array<{
    id: number
    sport_id: number
    name: string
    name_full: string
    abbreviation: string
    is_default: boolean
    is_goalie: boolean
    deleted_at: string | null
  }>
  officials: Array<any>
  teamOfficials: Array<{
    id: number
    sport_id: number
    name: string
    name_full: string
    abbreviation: string
    is_default: number
  }>
  periods: Array<{
    id: string
    name: string
    name_full: string
    is_overtime: boolean
  }>
  gameTypes: Array<{
    id: string
    name: string
    name_full: string
  }>
  goalTypes: Array<{
    id: string
    name: string
    name_full: string
    abbreviation: string
  }>
  cardTypes: Array<{
    id: string
    name: string
    name_full: string | null
    abbreviation: string
  }>
  undefined: Array<{
    id: string
    name: string
  }>
  genericSportProperties: Array<{
    id: string
    name: string
    background_web_image: string
    background_mobile_image: string
  }>
  defaultScheduleSettings: Array<{
    id: string
    name: string
    settings: {
      game: {
        timing: {
          default_game_length: number
          default_period_length: number
          default_overtime_length: number
        }
        scoring: {
          require_goal_type: boolean
          require_time_game_event: boolean
          require_goal_configuration: boolean
        }
        signatures: {
          skip_official: boolean
          skip_team_official: boolean
        }
        skip_lineup: boolean
        notification: {
          goals: number
          assist: number
          penalty: number
          final_score: number
        }
        require_officials: boolean
        require_waiver_signature: boolean
        require_unique_jersey_numbers: boolean
      }
      stats: {
        goals: {
          per: {
            forfeit: number
          }
        }
        points: {
          ties: number
          wins: number
          losses: number
          overtime_win: number
          overtime_loss: number
          shootout_loss: number
        }
      }
      standings: {
        sort_order: {
          stat_id: number
          tie_breaker_stats: Array<any>
        }
      }
    }
  }>
  statTypes: Array<{
    id: number
    name: string
    abbreviation: string
    description: string
    is_player_stat: boolean
    is_team_stat: boolean
  }>
  gameStatus: Array<{
    id: number
    name: string
  }>
}

/**
 * Interface representing a generic sport with basic properties.
 * @interface GenericSport
 */
interface GenericSport {
  id: string
  name: string
  background_web_image: string
  background_mobile_image: string
}

interface PlayerStatsBySeasonProps {
  players: DetailedPlayer[]
  sports: Sport[]
  genericSports: GenericSport[]
  shouldQueryForBothSkaterAndGoalieStats?: boolean
}

const PlayerStatsBySeason: React.FC<PlayerStatsBySeasonProps> = ({
  players,
  sports,
  genericSports,
  shouldQueryForBothSkaterAndGoalieStats = false,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  // Aggregate teams and sports across all players
  const playerSports = useMemo(() => {
    // Collect all unique teams across all players
    const allTeams = players?.flatMap((player) => player.teams)
    const mappedSports = allTeams.map((team) => {
      if (team.generic_sport_id) {
        const genericSport = genericSports.find(
          (s) => s.id === team.generic_sport_id
        )
        return {
          ...genericSport,
          sport_id: team.sport_id,
          is_generic: true,
          name_full: genericSport?.name,
          team: team,
        }
      }
      const lSport = sports.find((s) => s.id === team.sport_id)
      return {
        ...lSport,
        sport_id: team.sport_id,
        is_generic: false,
        name_full: lSport?.name_full,
        team: team,
      }
    })
    const uniqueSports = mappedSports.filter(
      (sport, index, self) =>
        // Filter unique sports by sport_id
        index === self.findIndex((s) => s.id === sport.id)
    )

    return uniqueSports
  }, [players, sports, genericSports])

  const [selectedSport, setSelectedSport] = useState(playerSports[0]?.id || '')

  const [allPlayerStats, setAllPlayerStats] = useState<any[]>([])
  const [allGoalieStats, setAllGoalieStats] = useState<any[]>([])
  const [statsLoading, setStatsLoading] = useState<boolean>(true)

  /**
   * Fetches statistics for all players of the selected sport
   * Memoized with useCallback to prevent unnecessary re-renders
   *
   * @returns {Promise<void>} - Promise that resolves when all stats are fetched
   */
  /**
   * Fetches statistics for all players of the selected sport
   * Uses Promise.all to fetch data in parallel for better performance
   *
   * @returns {Promise<void>} - Promise that resolves when all stats are fetched
   */
  const fetchAllPlayersStats = useCallback(async () => {
    if (!selectedSport) {
      setStatsLoading(false)
      return
    }

    if (!players || players.length === 0) {
      setStatsLoading(false)
      return
    }

    setStatsLoading(true)
    setAllPlayerStats([])
    setAllGoalieStats([])

    const selSport = playerSports.find((sport) => sport.id === selectedSport)

    const playersOfTheSelectedSport = players.filter((player) =>
      player.teams.some((team) => {
        if (selSport?.is_generic) {
          return (
            team.generic_sport_id === selSport?.id &&
            team.sport_id === selSport?.sport_id
          )
        }
        return team.sport_id === selSport?.sport_id
      })
    )

    // If no players found for the selected sport, clear loading state and return
    if (playersOfTheSelectedSport.length === 0) {
      setStatsLoading(false)
      return
    }

    try {
      // Create arrays to collect all stats
      let playerStatsArray: any[] = []
      let goalieStatsArray: any[] = []

      // Create an array of promises for all API calls
      const fetchPromises = playersOfTheSelectedSport.map(async (player) => {
        const is_skater =
          shouldQueryForBothSkaterAndGoalieStats || player?.is_skater
        const is_goalie =
          shouldQueryForBothSkaterAndGoalieStats || player?.is_goalie

        // Create an array to hold this player's fetch promises
        const playerPromises: Promise<void>[] = []

        // Only fetch player stats if player is a skater
        if (is_skater) {
          playerPromises.push(
            req(`/players/${player.id}/stats/profile/list`, {
              query: {
                goalie: 0,
                sort: 'desc',
                sortBy: 'points',
                sport_id: selSport?.sport_id,
                generic_sport_id: selSport?.is_generic
                  ? selSport?.id
                  : undefined,
              },
            })
              .then((playerResponse) => {
                // Filter to only use the items that are objects
                const filteredPlayerStats = Array.isArray(playerResponse)
                  ? playerResponse
                      .filter((item) => typeof item === 'object')
                      .filter((item) => item.sport_id === selSport?.sport_id)
                  : []

                if (selSport?.is_generic) {
                  playerStatsArray = [
                    ...playerStatsArray,
                    ...filteredPlayerStats.filter(
                      (stat) => stat.generic_sport_id === selSport?.id
                    ),
                  ]
                } else {
                  playerStatsArray = [
                    ...playerStatsArray,
                    ...filteredPlayerStats,
                  ]
                }
              })
              .catch((error) => {
                console.error(
                  `Error fetching player stats for player ${player.id}:`,
                  error
                )
              })
          )
        }

        // Only fetch goalie stats if player is a goalie
        if (is_goalie) {
          playerPromises.push(
            req(`/players/${player.id}/stats/profile/list`, {
              query: {
                goalie: 1,
                sort: 'desc',
                sortBy: 'points',
                sport_id: selSport?.sport_id,
                generic_sport_id: selSport?.is_generic
                  ? selSport?.id
                  : undefined,
              },
            })
              .then((goalieResponse) => {
                const filteredGoalieStats = Array.isArray(goalieResponse)
                  ? goalieResponse
                      .filter((item) => typeof item === 'object')
                      .filter((item) => item.sport_id === selSport?.sport_id)
                  : []

                if (selSport?.is_generic) {
                  goalieStatsArray = [
                    ...goalieStatsArray,
                    ...filteredGoalieStats.filter(
                      (stat) => stat.generic_sport_id === selSport?.id
                    ),
                  ]
                } else {
                  goalieStatsArray = [
                    ...goalieStatsArray,
                    ...filteredGoalieStats,
                  ]
                }
              })
              .catch((error) => {
                console.error(
                  `Error fetching goalie stats for player ${player.id}:`,
                  error
                )
              })
          )
        }

        // Return a promise that resolves when all this player's requests are done
        return Promise.all(playerPromises)
      })

      // Wait for all player fetch operations to complete
      await Promise.all(fetchPromises)

      // Update state with all collected stats
      setAllPlayerStats(playerStatsArray)
      setAllGoalieStats(goalieStatsArray)
    } catch (error) {
      console.error(`Error in fetchAllPlayersStats:`, error)
    } finally {
      setStatsLoading(false)
    }
  }, [
    players,
    selectedSport,
    shouldQueryForBothSkaterAndGoalieStats,
    playerSports,
  ])

  // When the selected sport changes, reset the stats and fetch for all players
  useEffect(() => {
    if (!selectedSport) {
      console.log('No sport selected')
      setStatsLoading(false)
      return
    }
    fetchAllPlayersStats()
  }, [selectedSport, players])

  // Check if the selected sport is a generic sport
  const isGenericSport = useMemo(() => {
    const selectedPlayerSport = playerSports.find(
      (sport) => sport.id === selectedSport
    )
    return selectedPlayerSport?.is_generic || false
  }, [playerSports, selectedSport])

  // Common columns for both tables
  const commonColumns = [
    {
      title: 'Team',
      dataIndex: 'team',
      key: 'team',
      width: isMobile ? 250 : '25%',
      render: (_, record) => (
        <Link
          to={`${ROUTES.TEAM_ROOT}/${record?.team?.id}`}
          css={css`
            text-decoration: none;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <img
              src={
                getImageThumbnailId(record.team) ||
                '/images/generic_placeholders/placeholder_team.png'
              }
              alt={record?.team?.name || record?.team?.name_full}
              css={css`
                width: 32px;
                height: 32px;
                border-radius: 8px;
                object-fit: contain;
                border: 1px solid ${colors.NEUTRAL_50};
                background-color: ${colors.NEUTRAL_0};
              `}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                const target = e.target as HTMLImageElement
                target.src = '/images/generic_placeholders/placeholder_team.png'
              }}
            />
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.NEUTRAL_0}
              >
                {record.team?.name}
              </Text>
              <Link
                to={`${ROUTES.SCHEDULE_ROOT}/${record?.season?.id}`}
                css={css`
                  text-decoration: none;
                `}
              >
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.ATTENDANCE_GRAY}
                >
                  {record.season?.name}
                </Text>
              </Link>
            </div>
          </div>
        </Link>
      ),
    },
  ]

  /**
   * Creates a column configuration for displaying a player statistic
   * @param {string} title - The display title for the column
   * @param {string} abbr - The abbreviation to match in the stats array
   * @param {string} defaultValue - Default value to display if the stat is not found
   * @param {string} tooltipText - Optional tooltip text to show on hover (defaults to the title)
   * @returns {Object} Column configuration for antd Table
   */
  const createStatColumn = (
    title,
    abbr,
    defaultValue = '0',
    tooltipText = ''
  ) => {
    // Helper function to get the label for a stat key based on sport and position
    const getStatLabel = (key, position = 'player') => {
      // First check if explicit tooltip text was provided
      if (tooltipText) {
        return tooltipText
      }

      // Check common labels
      if (statLabels.common[key]) {
        return statLabels.common[key]
      }

      // Check sport-specific labels
      const sportType = selectedSportObj?.name || 'generic'
      const positionType =
        position === 'goalie' || position === 'keeper'
          ? sportType === 'soccer'
            ? 'keeper'
            : 'goalie'
          : 'player'

      if (
        statLabels[sportType] &&
        statLabels[sportType][positionType] &&
        statLabels[sportType][positionType][key]
      ) {
        return statLabels[sportType][positionType][key]
      }

      // Check generic labels
      if (statLabels.generic.player[key]) {
        return statLabels.generic.player[key]
      }

      // If no label found, just return the key as fallback
      return title
    }

    return {
      title: () => (
        <Tooltip title={getStatLabel(abbr)} placement='top'>
          <span>{title}</span>
        </Tooltip>
      ),
      key: abbr.toLowerCase(),
      align: 'center' as const,
      width: 80, // Set a fixed width for stat columns
      render: (_, record) => {
        const stat = record.stats?.find(
          (s) => s.type?.abbr === abbr || s.abbr === abbr
        )
        return (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={colors.NEUTRAL_0}
            >
              {stat?.value || defaultValue}
            </Text>
          </div>
        )
      },
    }
  }

  // Generic sport columns - Games Played, Points, Points per Game
  const genericSportColumns = [
    ...commonColumns,
    createStatColumn('GP', 'GP', '0', 'Games Played'),
    createStatColumn('Points', 'P', '0', 'Total Points'),
    createStatColumn('PTS/G', 'PTS/G', '0.00', 'Average Points Per Game'),
  ]

  /**
   * Determines which columns to display based on the selected sport
   * @returns {Array} Array of column configurations for the player stats table
   */
  const getSportSpecificColumns = () => {
    // If generic sport, use the generic columns
    if (isGenericSport) {
      return genericSportColumns
    }

    // Get the selected sport
    const selectedPlayerSport = playerSports.find(
      (sport) => sport.sport_id === selectedSport
    )

    // Return different columns based on sport id
    if (selectedPlayerSport) {
      // Soccer player columns
      if (selectedPlayerSport?.name === 'soccer') {
        return [
          ...commonColumns,
          createStatColumn('MP', 'MP', '0', 'Matches Played'),
          createStatColumn('G', 'G', '0', 'Goals'),
          createStatColumn('A', 'A', '0', 'Assists'),
          createStatColumn('GFA', 'GFA', '0', 'Goals For Average'),
          createStatColumn('FC', 'FC', '0', 'Fouls Committed'),
          createStatColumn('YC', 'YC', '0', 'Yellow Cards'),
          createStatColumn('RC', 'RC', '0', 'Red Cards'),
          createStatColumn('BC', 'BC', '0', 'Blue Cards'),
        ]
      }

      // Default player columns for other sports (hockey, etc.)
      return [
        ...commonColumns,
        createStatColumn('GP', 'GP', '0', 'Games Played'),
        createStatColumn('G', 'G', '0', 'Goals'),
        createStatColumn('A', 'A', '0', 'Assists'),
        createStatColumn('P', 'P', '0', 'Points'),
        createStatColumn('PiM', 'PiM', '0', 'Penalty Minutes'),
        createStatColumn('PPG', 'PPG', '0', 'Power Play Goals'),
        createStatColumn('PTS/G', 'PTS/G', '0.00', 'Points Per Game'),
      ]
    }

    // Fallback to common columns
    return commonColumns
  }

  // Get the appropriate columns based on the sport type
  const playerColumns = getSportSpecificColumns()

  /**
   * Determines which columns to display for goalie stats based on the selected sport
   * @returns {Array} Array of column configurations for the goalie stats table
   */
  const getGoalieColumns = () => {
    if (isGenericSport) {
      return commonColumns // Generic sports should never reach here since we only show player stats
    }

    // Get the selected sport
    const selectedPlayerSport = playerSports.find(
      (sport) => sport.sport_id === selectedSport
    )

    if (selectedPlayerSport) {
      const sportId = selectedPlayerSport.sport_id

      // Soccer goalie columns
      if (sportId === 'soccer') {
        return [
          ...commonColumns,
          createStatColumn('MP', 'MP', '0', 'Matches Played'),
          createStatColumn('GA', 'GA', '0', 'Goals Against'),
          createStatColumn('GAA', 'GAA', '0.00', 'Goals Against Average'),
          createStatColumn('SV%', 'SV%', '0.000', 'Save Percentage'),
          createStatColumn('SV', 'SV', '0', 'Saves'),
          createStatColumn('CS', 'CS', '0', 'Clean Sheets'),
          createStatColumn('W', 'W', '0', 'Wins'),
          createStatColumn('L', 'L', '0', 'Losses'),
          createStatColumn('D', 'D', '0', 'Draws'),
        ]
      }

      // Default goalie columns for other sports (hockey, etc.)
      return [
        ...commonColumns,
        createStatColumn('GP', 'GP', '0', 'Games Played'),
        createStatColumn('W', 'W', '0', 'Wins'),
        createStatColumn('L', 'L', '0', 'Losses'),
        createStatColumn('T', 'T', '0', 'Ties'),
        createStatColumn('OTL', 'OTL', '0', 'Overtime Losses'),
        createStatColumn('GAA', 'GAA', '0.00', 'Goals Against Average'),
        createStatColumn('SV%', 'SV%', '0.000', 'Save Percentage'),
        createStatColumn('SO', 'SO', '0', 'Shutouts'),
        createStatColumn('Min', 'MP', '0', 'Minutes Played'),
        createStatColumn('PiM', 'PiM', '0', 'Penalty Minutes'),
        createStatColumn('SA', 'SA', '0', 'Shots Against'),
        createStatColumn('SV', 'SV', '0', 'Saves'),
        createStatColumn('GA', 'GA', '0', 'Goals Against'),
      ]
    }

    // Fallback to basic goalie columns
    return [
      ...commonColumns,
      createStatColumn('GP', 'GP', '0', 'Games Played'),
      createStatColumn('GA', 'GA', '0', 'Goals Against'),
      createStatColumn('GAA', 'GAA', '0.00', 'Goals Against Average'),
      createStatColumn('SV', 'SV', '0', 'Saves'),
      createStatColumn('SV%', 'SV%', '0.000', 'Save Percentage'),
    ]
  }

  const goalieColumns = getGoalieColumns()

  /**
   * Calculates summary statistics by summing values across all records
   * @param {Array} stats - Array of player or goalie stats
   * @param {Array} statKeys - Array of stat abbreviations to include in the summary
   * @returns {Object} Object containing the summary statistics with the statKey as the keys
   */
  const calculateSummaryStats = (stats, statKeys) => {
    if (!stats || !stats.length || !statKeys || !statKeys.length) {
      return null
    }

    const summary = {}

    // Initialize summary object with 0 values
    statKeys.forEach((key) => {
      summary[key] = 0
    })

    // Sum up values for each statistic
    stats.forEach((record) => {
      statKeys.forEach((key) => {
        const statValue =
          record.stats?.find((s) => s.type?.abbr === key || s.abbr === key)
            ?.value || '0'

        // If the value is a number, add it to the sum
        // If it's a percentage or ratio, we don't sum these
        if (!key.includes('%') && !key.includes('/')) {
          summary[key] += parseFloat(statValue) || 0
        }
      })
    })

    return summary
  }

  /**
   * Creates a summary table for the given stats
   * @param {Object} summaryStats - Object containing the summary statistics
   * @param {Array} displayOrder - Array of stats abbreviations in the desired display order
   * @param {string} title - Title for the summary table
   * @returns {JSX.Element} A table component displaying the summary statistics
   */
  const renderSummaryTable = (summaryStats, displayOrder, title) => {
    if (!summaryStats) {
      return null
    }

    // Helper function to get the label for a stat key based on sport and position
    const getStatLabel = (key) => {
      // First check common labels
      if (statLabels.common[key]) {
        return statLabels.common[key]
      }

      // Then check sport-specific labels
      const sportType = selectedSportObj?.name || 'generic'
      const positionType =
        title.toLowerCase() === 'goalie' || title.toLowerCase() === 'keeper'
          ? sportType === 'soccer'
            ? 'keeper'
            : 'goalie'
          : 'player'

      if (
        statLabels[sportType] &&
        statLabels[sportType][positionType] &&
        statLabels[sportType][positionType][key]
      ) {
        return statLabels[sportType][positionType][key]
      }

      // Finally check generic labels
      if (statLabels.generic.player[key]) {
        return statLabels.generic.player[key]
      }

      // If no label found, just return the key as fallback
      return key
    }

    // Create columns for the summary table
    const columns = displayOrder.map((key) => ({
      title: () => {
        // Check if we need to display "MiN" instead of "MP" for non-generic, non-soccer goalie stats
        const displayKey =
          key === 'MP' &&
          !isGenericSport &&
          selectedSportObj?.name !== 'soccer' &&
          (title.toLowerCase() === 'goalie' || title.toLowerCase() === 'keeper')
            ? 'Min'
            : key

        return (
          <Tooltip title={getStatLabel(key)} placement='top'>
            <span>{displayKey}</span>
          </Tooltip>
        )
      },
      dataIndex: key,
      key: key,
      align: 'center' as const,
      width: 80, // Set a fixed width for summary stat columns
      render: (value) => (
        <Text
          variant='paragraphMedium'
          weight='semiBold'
          color={colors.NEUTRAL_0}
        >
          {value}
        </Text>
      ),
    }))

    // Create a single row of data with all the summary values
    const dataSource = [
      {
        key: 'summary',
        ...summaryStats,
      },
    ]

    return (
      <div
        css={css`
          margin-bottom: 16px;
        `}
      >
        <div
          css={css`
            margin-bottom: 8px;
          `}
        >
          <Text variant='heading5' weight='semiBold' color={colors.NEUTRAL_0}>
            {title}
          </Text>
        </div>
        <div
          css={css`
            overflow-x: auto;
            width: 100%;
          `}
        >
          <Table
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            bordered={true}
            size='small'
            scroll={{ x: 'max-content' }}
            showHeader={true}
            css={css`
              .ant-table {
                border-radius: 8px;
                overflow: hidden;
              }
              .ant-table-container {
                border-radius: 8px;
                overflow: hidden;
              }
              .ant-table-thead > tr > th {
                background-color: ${colors.SECONDARY_400};
                color: ${colors.NEUTRAL_0};
                text-align: center;
              }
              .ant-table-thead > tr > th:first-child {
                border-top-left-radius: 8px;
              }
              .ant-table-thead > tr > th:last-child {
                border-top-right-radius: 8px;
              }
              .ant-table-tbody > tr > td {
                vertical-align: middle;
                background-color: ${colors.SECONDARY_500};
              }
              .ant-table-tbody > tr:last-child > td:first-child {
                border-bottom-left-radius: 8px;
              }
              .ant-table-tbody > tr:last-child > td:last-child {
                border-bottom-right-radius: 8px;
              }
              .ant-table-tbody > tr:hover > td {
                background-color: ${colors.SECONDARY_400} !important;
              }
            `}
          />
        </div>
      </div>
    )
  }

  // Get the selected sport for summary tables
  const selectedSportObj = useMemo(
    () => playerSports.find((sport) => sport.sport_id === selectedSport),
    [playerSports, selectedSport]
  )

  // Define summary stats based on sport type
  const playerSummaryStats = useMemo(() => {
    if (isGenericSport) {
      return calculateSummaryStats(allPlayerStats, ['GP', 'P'])
    } else if (selectedSportObj?.name === 'soccer') {
      return calculateSummaryStats(allPlayerStats, [
        'MP',
        'G',
        'A',
        'FC',
        'RC',
        'YC',
        'BC',
      ])
    } else {
      // Default to hockey/other sports
      return calculateSummaryStats(allPlayerStats, [
        'GP',
        'G',
        'A',
        'P',
        'PiM',
        'PPG',
      ])
    }
  }, [allPlayerStats, isGenericSport, selectedSportObj])

  const goalieSummaryStats = useMemo(() => {
    if (selectedSportObj?.name === 'soccer') {
      return calculateSummaryStats(allGoalieStats, [
        'MP',
        'GA',
        'SV',
        'CS',
        'W',
        'L',
        'D',
      ])
    } else {
      // Default to hockey/other sports
      return calculateSummaryStats(allGoalieStats, [
        'GP',
        'W',
        'L',
        'T',
        'OTL',
        'SO',
        'MP',
        'PiM',
        'SA',
        'SV',
        'GA',
      ])
    }
  }, [allGoalieStats, selectedSportObj])

  // Define labels for summary stats
  const statLabels = {
    // Common labels used across multiple sports
    common: {
      GP: 'Games Played',

      W: 'Wins',
      L: 'Losses',
      T: 'Ties',
      D: 'Draws',
      G: 'Goals',
      A: 'Assists',
      P: 'Points',
      PiM: 'Penalty Minutes',
    },

    // Hockey specific labels
    hockey: {
      // Player specific
      player: {
        PPG: 'Power Play Goals',
        'PTS/G': 'Points Per Game',
      },
      // Goalie specific
      goalie: {
        OTL: 'Overtime Losses',
        SO: 'Shutouts',
        MIN: 'Minutes Played',
        SA: 'Shots Against',
        SV: 'Saves',
        GA: 'Goals Against',
        GAA: 'Goals Against Average',
        'SV%': 'Save Percentage',
        MP: 'Minutes Played',
      },
    },

    // Soccer specific labels
    soccer: {
      MP: 'Matches Played',
      // Player specific
      player: {
        FC: 'Fouls Committed',
        YC: 'Yellow Cards',
        RC: 'Red Cards',
        BC: 'Blue Cards',
        GFA: 'Goals For Average',
      },
      // Keeper (goalie) specific
      keeper: {
        CS: 'Clean Sheets',
        GA: 'Goals Against',
        GAA: 'Goals Against Average',
        SV: 'Saves',
        'SV%': 'Save Percentage',
      },
    },

    // Generic sport labels
    generic: {
      player: {
        'PTS/G': 'Average Points Per Game',
      },
    },
  }

  // Define display order for summary stats
  const getPlayerDisplayOrder = () => {
    if (isGenericSport) {
      return ['GP', 'P']
    } else if (selectedSportObj?.name === 'soccer') {
      return ['MP', 'G', 'A', 'FC', 'YC', 'RC', 'BC']
    } else {
      return ['GP', 'G', 'A', 'P', 'PiM', 'PPG']
    }
  }

  const getGoalieDisplayOrder = () => {
    if (selectedSportObj?.name === 'soccer') {
      return ['MP', 'W', 'L', 'D', 'GA', 'SV', 'CS']
    } else {
      return ['GP', 'W', 'L', 'T', 'OTL', 'SO', 'MP', 'PiM', 'SA', 'SV', 'GA']
    }
  }

  // Get the player title based on the sport type
  const getPlayerTitle = () => {
    if (isGenericSport || selectedSportObj?.name === 'soccer') {
      return 'Player'
    } else {
      if (selectedSportObj?.name === 'hockey') {
        return 'Skater'
      }
      return 'Player'
    }
  }

  // Get the goalie title based on the sport type
  const getGoalieTitle = () => {
    return selectedSportObj?.name === 'soccer' ? 'Keeper' : 'Goalie'
  }

  return (
    <div
      className='stats-by-season'
      css={css`
        display: flex;
        flex-direction: column;
        min-height: 98px;
        margin-top: 24px;
        padding: 20px 16px;
        background-color: ${colors.SECONDARY_300};
        border: 1px solid #3f4753;
        border-radius: 8px;
        ${isMobile &&
        css`
          padding: 16px 8px;
        `}
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          align-self: stretch;
          padding-bottom: 16px;
          margin-bottom: 16px;
          border-bottom: 1px solid ${colors.SECONDARY_200};

          ${isMobile &&
          css`
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          `}
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          `}
        >
          <Text variant='heading4' weight='semiBold' color={colors.NEUTRAL_0}>
            CAREER STATISTICS
          </Text>
          {/* <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.ATTENDANCE_GRAY}
          >
            {players.length > 1
              ? 'Players Career Statistics'
              : 'Player Career Statistics'}
          </Text> */}
        </div>
        <div
          css={css`
            min-width: 200px;
            ${isMobile &&
            css`
              width: 100%;
            `}
            ${!selectedSport &&
            css`
              display: none;
            `}
          `}
        >
          <SelectInput
            label=''
            name='sport'
            value={selectedSport || ''}
            options={Array.from(
              new Map(
                playerSports.map((sport) => [
                  sport.id,
                  {
                    label: sport.name_full,
                    value: sport.id,
                  },
                ])
              ).values()
            )}
            disabled={false}
            onChange={(value) => {
              setSelectedSport(value)
            }}
            fieldErrors={[]}
            customError={{}}
          />
        </div>
      </div>

      {/* Loading state */}
      {statsLoading && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 200px;
          `}
        >
          <Spin size='large' />
        </div>
      )}

      {/* Player Summary Stats */}
      {!statsLoading &&
        allPlayerStats &&
        allPlayerStats.length > 0 &&
        playerSummaryStats &&
        renderSummaryTable(
          playerSummaryStats,
          getPlayerDisplayOrder(),
          getPlayerTitle()
        )}

      {/* Goalie Summary Stats */}
      {!statsLoading &&
        !isGenericSport &&
        allGoalieStats &&
        allGoalieStats.length > 0 &&
        goalieSummaryStats &&
        renderSummaryTable(
          goalieSummaryStats,
          getGoalieDisplayOrder(),
          getGoalieTitle()
        )}

      {/* Player Stats Table */}
      {!statsLoading && allPlayerStats && allPlayerStats.length > 0 && (
        <div
          css={css`
            margin-top: 24px;
          `}
        >
          <div
            css={css`
              margin-bottom: 8px;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='semiBold'
              color={colors.NEUTRAL_0}
            >
              {`${getPlayerTitle()} Statistics`}
            </Text>
          </div>
          <div
            css={css`
              overflow-x: auto;
              width: 100%;
              ${isMobile &&
              allGoalieStats?.length === 0 &&
              css`
                margin-bottom: 100px;
              `}
            `}
          >
            <Table
              dataSource={allPlayerStats
                // sort from most recent to the old, using season?.starts_at, which is a date string
                .sort((a, b) => {
                  const dateA = new Date(a.season?.starts_at)
                  const dateB = new Date(b.season?.starts_at)
                  return dateB.getTime() - dateA.getTime()
                })
                // .sort((a, b) => a.team.name.localeCompare(b.team.name))
                .map((stat, index) => ({
                  ...stat,
                  key: index,
                }))}
              columns={playerColumns}
              pagination={false}
              bordered={true}
              size='small'
              scroll={{ x: 'max-content' }}
              css={css`
                .ant-table {
                  border-radius: 8px;
                  overflow: hidden;
                }
                .ant-table-container {
                  border-radius: 8px;
                  overflow: hidden;
                }
                .ant-table-thead > tr > th {
                  background-color: ${colors.SECONDARY_500};
                  color: ${colors.NEUTRAL_0};
                  text-align: center;
                }
                .ant-table-thead > tr > th:first-child {
                  border-top-left-radius: 8px;
                  text-align: left;
                  position: sticky;
                  left: 0;
                  z-index: 1;
                }
                .ant-table-thead > tr > th:last-child {
                  border-top-right-radius: 8px;
                }
                .ant-table-tbody > tr > td {
                  vertical-align: middle;
                }
                .ant-table-tbody > tr:nth-child(odd) > td {
                  background-color: ${colors.SECONDARY_500};
                }
                .ant-table-tbody > tr:nth-child(even) > td {
                  background-color: ${colors.SECONDARY_400};
                }
                .ant-table-tbody > tr > td:first-child {
                  position: sticky;
                  left: 0;
                  z-index: 1;
                }
                .ant-table-tbody > tr:nth-child(odd) > td:first-child {
                  background-color: ${colors.SECONDARY_500};
                }
                .ant-table-tbody > tr:nth-child(even) > td:first-child {
                  background-color: ${colors.SECONDARY_400};
                }
                .ant-table-tbody > tr:last-child > td:first-child {
                  border-bottom-left-radius: 8px;
                }
                .ant-table-tbody > tr:last-child > td:last-child {
                  border-bottom-right-radius: 8px;
                }
                .ant-table-tbody > tr:hover > td {
                  background-color: ${colors.SECONDARY_200} !important;
                }
              `}
            />
          </div>
        </div>
      )}

      {/* Goalie Stats Table - Only show if not a generic sport and has data */}
      {!statsLoading &&
        !isGenericSport &&
        allGoalieStats &&
        allGoalieStats.length > 0 && (
          <div
            css={css`
              margin-top: 24px;
            `}
          >
            <div
              css={css`
                margin-bottom: 8px;
              `}
            >
              <Text
                variant='paragraphMedium'
                weight='semiBold'
                color={colors.NEUTRAL_0}
              >
                {`${getGoalieTitle()} Statistics`}
              </Text>
            </div>
            <div
              css={css`
                overflow-x: auto;
                width: 100%;
                ${isMobile &&
                css`
                  margin-bottom: 100px;
                `}
              `}
            >
              <Table
                dataSource={allGoalieStats
                  .sort((a, b) => {
                    const dateA = new Date(a.season?.starts_at)
                    const dateB = new Date(b.season?.starts_at)
                    return dateB.getTime() - dateA.getTime()
                  })
                  // .sort((a, b) => a?.team?.name?.localeCompare(b?.team?.name))
                  .map((stat, index) => ({
                    ...stat,
                    key: index,
                  }))}
                columns={goalieColumns}
                pagination={false}
                bordered={true}
                size='small'
                scroll={{ x: 'max-content' }}
                css={css`
                  .ant-table {
                    border-radius: 8px;
                    overflow: hidden;
                  }
                  .ant-table-container {
                    border-radius: 8px;
                    overflow: hidden;
                  }
                  .ant-table-thead > tr > th {
                    background-color: ${colors.SECONDARY_500};
                    color: ${colors.NEUTRAL_0};
                    text-align: center;
                  }
                  .ant-table-thead > tr > th:first-child {
                    border-top-left-radius: 8px;
                    text-align: left;
                    position: sticky;
                    left: 0;
                    z-index: 1;
                  }
                  .ant-table-thead > tr > th:last-child {
                    border-top-right-radius: 8px;
                  }
                  .ant-table-tbody > tr > td {
                    vertical-align: middle;
                  }
                  .ant-table-tbody > tr:nth-child(odd) > td {
                    background-color: ${colors.SECONDARY_500};
                  }
                  .ant-table-tbody > tr:nth-child(even) > td {
                    background-color: ${colors.SECONDARY_400};
                  }
                  .ant-table-tbody > tr > td:first-child {
                    position: sticky;
                    left: 0;
                    z-index: 1;
                  }
                  .ant-table-tbody > tr:nth-child(odd) > td:first-child {
                    background-color: ${colors.SECONDARY_500};
                  }
                  .ant-table-tbody > tr:nth-child(even) > td:first-child {
                    background-color: ${colors.SECONDARY_400};
                  }
                  .ant-table-tbody > tr:last-child > td:first-child {
                    border-bottom-left-radius: 8px;
                  }
                  .ant-table-tbody > tr:last-child > td:last-child {
                    border-bottom-right-radius: 8px;
                  }
                  .ant-table-tbody > tr:hover > td {
                    background-color: ${colors.SECONDARY_200} !important;
                  }
                `}
              />
            </div>
          </div>
        )}

      {/* No data message */}
      {!statsLoading &&
        !allPlayerStats?.length &&
        (!allGoalieStats?.length || isGenericSport) && (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              height: 200px;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.ATTENDANCE_GRAY}
            >
              No Statistics Available
            </Text>
          </div>
        )}
    </div>
  )
}

const mapStateToProps = (state) => {
  const playerTypes = getAllPlayerTypes(state)
  const sports = Object.values(getSports(state))
  const genericSports = getAllGenericSportProperties(state)

  return {
    playerTypes,
    sports,
    genericSports,
  }
}

export default connect(mapStateToProps, null)(PlayerStatsBySeason)
